import { router } from "expo-router";
import React, { useCallback, type ReactNode } from "react";
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  View,
  type StyleProp,
  type ViewStyle,
} from "react-native";

type LegalRoute =
  | "/terms"
  | "/privacy"
  | "/refund-policy"
  | "/listing-guidelines";

type LegalAcceptanceProps = {
  checked: boolean;
  disabled?: boolean;
  error?: string | null;
  loading?: boolean;
  onChange: (checked: boolean) => void;
  returnTo: string;
  saving?: boolean;
  style?: StyleProp<ViewStyle>;
};

type LegalPolicyLinkProps = {
  children: ReactNode;
  pathname: LegalRoute;
  returnTo: string;
};

function LegalPolicyLink({
  children,
  pathname,
  returnTo,
}: LegalPolicyLinkProps) {
  const openPolicy = useCallback(() => {
    router.push({
      pathname,
      params: { returnTo },
    } as any);
  }, [pathname, returnTo]);

  return (
    <Text accessibilityRole="link" onPress={openPolicy} style={styles.link}>
      {children}
    </Text>
  );
}

export function LegalAcceptance({
  checked,
  disabled,
  error,
  loading,
  onChange,
  returnTo,
  saving,
  style,
}: LegalAcceptanceProps) {
  const busy = loading || saving;
  const isDisabled = disabled || busy;

  const toggleChecked = useCallback(() => {
    if (isDisabled) return;
    onChange(!checked);
  }, [checked, isDisabled, onChange]);

  return (
    <View style={[styles.wrap, style]}>
      <View style={styles.row}>
        <Pressable
          accessibilityHint="Select to accept the linked legal policies."
          accessibilityLabel="Accept Terms of Use, Privacy Policy, Refund Policy, and Listing Guidelines"
          accessibilityRole="checkbox"
          accessibilityState={{ checked, disabled: isDisabled }}
          disabled={isDisabled}
          hitSlop={8}
          onPress={toggleChecked}
          style={[
            styles.checkbox,
            checked ? styles.checkboxChecked : null,
            isDisabled ? styles.checkboxDisabled : null,
          ]}
        >
          {checked ? <View style={styles.checkboxFill} /> : null}
        </Pressable>

        <Text style={styles.text}>
          {"I accept the "}
          <LegalPolicyLink pathname="/terms" returnTo={returnTo}>
            Terms of Use
          </LegalPolicyLink>
          {", "}
          <LegalPolicyLink pathname="/privacy" returnTo={returnTo}>
            Privacy Policy
          </LegalPolicyLink>
          {", "}
          <LegalPolicyLink pathname="/refund-policy" returnTo={returnTo}>
            Refund Policy
          </LegalPolicyLink>
          {", and "}
          <LegalPolicyLink pathname="/listing-guidelines" returnTo={returnTo}>
            Listing Guidelines
          </LegalPolicyLink>
          {"."}
        </Text>
      </View>

      {busy ? (
        <View style={styles.statusRow}>
          <ActivityIndicator size="small" />
          <Text style={styles.statusText}>
            {saving ? "Saving legal acceptance..." : "Checking legal acceptance..."}
          </Text>
        </View>
      ) : null}

      {error ? (
        <Text accessibilityRole="alert" style={styles.errorText}>
          {error}
        </Text>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 14,
    backgroundColor: "#FFFFFF",
    padding: 12,
  },

  row: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
  },

  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: "#9CA3AF",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FFFFFF",
    marginTop: 1,
  },

  checkboxChecked: {
    borderColor: "#0F766E",
    backgroundColor: "#0F766E",
  },

  checkboxDisabled: {
    opacity: 0.7,
  },

  checkboxFill: {
    width: 10,
    height: 10,
    borderRadius: 3,
    backgroundColor: "#FFFFFF",
  },

  text: {
    flex: 1,
    minWidth: 0,
    color: "#374151",
    fontSize: 13,
    fontWeight: "700",
    lineHeight: 20,
  },

  link: {
    color: "#0F766E",
    fontWeight: "900",
    textDecorationLine: "underline",
  },

  statusRow: {
    marginTop: 10,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },

  statusText: {
    flex: 1,
    color: "#6B7280",
    fontSize: 12,
    fontWeight: "700",
    lineHeight: 18,
  },

  errorText: {
    marginTop: 10,
    color: "#991B1B",
    fontSize: 12,
    fontWeight: "800",
    lineHeight: 18,
  },
});
